// MeusChamados.js — Redesign Visual Completo

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { db } from './firebase';
import { collection, query, where, getDocs, orderBy, limit } from './firestore-compat';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const STATUS_CONFIG = {
    'Aberto':               { color: '#3b82f6', bg: '#eff6ff', icon: '🔵' },
    'Em Andamento':         { color: '#f59e0b', bg: '#fffbeb', icon: '🟡' },
    'Pendente de Retorno':  { color: '#8b5cf6', bg: '#f5f3ff', icon: '🟣' },
    'Concluído':            { color: '#10b981', bg: '#ecfdf5', icon: '🟢' },
};

const PRIORITY_CONFIG = {
    'Baixa':   { color: '#6b7280', bg: '#f3f4f6' },
    'Média':   { color: '#f59e0b', bg: '#fffbeb' },
    'Alta':    { color: '#ef4444', bg: '#fef2f2' },
    'Crítico': { color: '#dc2626', bg: '#fef2f2' },
};

function formatarData(timestamp) {
    if (!timestamp) return 'N/A';
    try {
        return format(new Date(timestamp.seconds * 1000), "dd 'de' MMM 'de' yyyy", { locale: ptBR });
    } catch { return 'N/A'; }
}

function ChamadoCard({ chamado }) {
    const statusCfg = STATUS_CONFIG[chamado.status] || STATUS_CONFIG['Aberto'];
    const prioCfg = PRIORITY_CONFIG[chamado.priority] || PRIORITY_CONFIG['Média'];

    return (
        <div className="chamado-card-novo" style={{
            background: '#fff',
            borderRadius: '16px',
            border: '1px solid #e5e7eb',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            transition: 'box-shadow 0.2s',
            position: 'relative',
            overflow: 'hidden',
        }}>
            {/* Faixa lateral de status */}
            <div style={{
                position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px',
                background: statusCfg.color, borderRadius: '4px 0 0 4px'
            }} />

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 600, letterSpacing: '0.5px' }}>
                        CHAMADO #{chamado.idPublico}
                    </span>
                    <h3 style={{ margin: '4px 0 0', fontSize: '15px', fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
                        {chamado.title}
                    </h3>
                </div>
                <span style={{
                    padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                    color: prioCfg.color, background: prioCfg.bg, whiteSpace: 'nowrap', marginLeft: '8px'
                }}>
                    {chamado.priority}
                </span>
            </div>

            {/* Linha de info */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: '#6b7280' }}>
                <span>📁 {chamado.category}</span>
                <span>📅 {formatarData(chamado.createdAt)}</span>
                {chamado.assignedToName && <span>👤 {chamado.assignedToName}</span>}
            </div>

            {/* Status badge */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                    color: statusCfg.color, background: statusCfg.bg, border: `1px solid ${statusCfg.color}30`
                }}>
                    {statusCfg.icon} {chamado.status}
                </span>
                {chamado.lastMessageBy === 'responsavel' && (
                    <span style={{
                        padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                        background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca',
                        animation: 'pulse 2s infinite'
                    }}>
                        💬 Nova Resposta
                    </span>
                )}
            </div>

            <Link
                to={`/chamado/${chamado.id}`}
                state={{ from: '/meus-chamados' }}
                style={{
                    display: 'block', textAlign: 'center', padding: '10px',
                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px',
                    color: '#3b82f6', fontWeight: 600, fontSize: '13px', textDecoration: 'none',
                    transition: 'background 0.15s',
                }}
            >
                Ver Conversa / Detalhes →
            </Link>
        </div>
    );
}

function MeusChamados() {
    const [ticketNumber, setTicketNumber] = useState('');
    const [email, setEmail] = useState('');
    const [chamadosList, setChamadosList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [viewMode, setViewMode] = useState('login');
    const [activeTab, setActiveTab] = useState('ativos'); // 'ativos' ou 'historico'

    useEffect(() => {
        const savedEmail = localStorage.getItem('chamados_user_email');
        if (savedEmail) setEmail(savedEmail);
    }, []);

    const handleSearch = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        setChamadosList([]);

        if (!ticketNumber || !email) {
            setMessage('Preencha o e-mail e um número de chamado para acessar.');
            setIsLoading(false);
            return;
        }

        const numero = parseInt(ticketNumber.replace('#', '').trim());
        const emailNormalizado = email.toLowerCase().trim();

        try {
            const chamadosRef = collection(db, 'chamados');
            const qVerifica = query(
                chamadosRef,
                where('idPublico', '==', numero),
                where('solicitanteEmail', '==', emailNormalizado),
                limit(1)
            );
            const snapshotVerifica = await getDocs(qVerifica);

            if (snapshotVerifica.empty) {
                setMessage('Dados incorretos. Verifique o número do chamado e o e-mail.');
                setIsLoading(false);
                return;
            }

            localStorage.setItem('chamados_user_email', emailNormalizado);

            const qTodos = query(
                chamadosRef,
                where('solicitanteEmail', '==', emailNormalizado),
                orderBy('createdAt', 'desc'),
                limit(20)
            );
            const snapshotTodos = await getDocs(qTodos);
            setChamadosList(snapshotTodos.docs.map(d => ({ id: d.id, ...d.data() })));
            setViewMode('dashboard');
        } catch (error) {
            console.error('Erro ao consultar chamados: ', error);
            setMessage('Erro de conexão. Tente novamente.');
        } finally {
            setIsLoading(false);
        }
    };

    const sair = () => {
        setViewMode('login');
        setChamadosList([]);
        setMessage('');
        setTicketNumber('');
    };

    const colunas = useMemo(() => ({
        abertos:     chamadosList.filter(c => c.status === 'Aberto'),
        emAndamento: chamadosList.filter(c => ['Em Andamento', 'Pendente de Retorno'].includes(c.status)),
        concluidos:  chamadosList.filter(c => c.status === 'Concluído'),
    }), [chamadosList]);

    const styles = {
        page: {
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f0f4ff 0%, #fafafa 100%)',
            fontFamily: "'Inter', -apple-system, sans-serif",
        },
        topBar: {
            background: '#fff',
            borderBottom: '1px solid #e5e7eb',
            padding: '14px 32px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        brand: { fontSize: '18px', fontWeight: 800, color: '#1e40af', letterSpacing: '-0.5px' },
        content: { maxWidth: '1000px', margin: '0 auto', padding: '40px 24px' },
    };

    return (
        <div style={styles.page}>
            {/* Top Bar */}
            <div style={styles.topBar}>
                <span style={styles.brand}>🔵 Quality Portal</span>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {viewMode === 'dashboard' && (
                        <button
                            onClick={sair}
                            style={{
                                padding: '7px 14px', borderRadius: '8px', border: '1px solid #d1d5db',
                                background: '#fff', color: '#374151', fontSize: '13px', cursor: 'pointer', fontWeight: 600,
                            }}
                        >
                            ← Nova Consulta
                        </button>
                    )}
                    <Link to="/login" style={{
                        padding: '7px 16px', borderRadius: '8px', background: '#1e40af',
                        color: '#fff', fontSize: '13px', textDecoration: 'none', fontWeight: 600,
                    }}>
                        Ir para o Sistema
                    </Link>
                    <Link to="/abrir-chamado" style={{
                        padding: '7px 16px', borderRadius: '8px', background: '#10b981',
                        color: '#fff', fontSize: '13px', textDecoration: 'none', fontWeight: 600,
                    }}>
                        + Abrir Chamado
                    </Link>
                </div>
            </div>

            <div style={styles.content}>

                {/* TELA DE CONSULTA */}
                {viewMode === 'login' && (
                    <div style={{ maxWidth: '480px', margin: '0 auto' }}>
                        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔍</div>
                            <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#111827', margin: '0 0 8px' }}>
                                Acompanhe sua Solicitação
                            </h1>
                            <p style={{ color: '#6b7280', fontSize: '15px', margin: 0 }}>
                                Acesse o histórico dos seus chamados usando seu e-mail e número de protocolo.
                            </p>
                        </div>

                        <form onSubmit={handleSearch} style={{
                            background: '#fff', borderRadius: '20px', padding: '32px',
                            boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6',
                        }}>
                            <div style={{ marginBottom: '18px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                    Seu E-mail de Cadastro
                                </label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="exemplo@empresa.com.br"
                                    required
                                    style={{
                                        width: '100%', padding: '12px 14px', borderRadius: '10px',
                                        border: '1.5px solid #e5e7eb', fontSize: '14px', outline: 'none',
                                        boxSizing: 'border-box', color: '#111827',
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '8px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                                    Número do Protocolo (Chamado)
                                </label>
                                <input
                                    type="text"
                                    value={ticketNumber}
                                    onChange={(e) => setTicketNumber(e.target.value)}
                                    placeholder="Ex: 1042"
                                    required
                                    style={{
                                        width: '100%', padding: '12px 14px', borderRadius: '10px',
                                        border: '1.5px solid #e5e7eb', fontSize: '14px', outline: 'none',
                                        boxSizing: 'border-box', color: '#111827',
                                    }}
                                />
                                <p style={{ fontSize: '12px', color: '#9ca3af', margin: '6px 0 0' }}>
                                    Use o número de qualquer chamado que você já abriu para confirmar sua identidade.
                                </p>
                            </div>

                            {message && (
                                <div style={{
                                    padding: '12px 14px', borderRadius: '10px', background: '#fef2f2',
                                    border: '1px solid #fecaca', color: '#dc2626', fontSize: '13px', marginBottom: '16px',
                                }}>
                                    ❌ {message}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isLoading}
                                style={{
                                    width: '100%', padding: '13px', borderRadius: '12px', border: 'none',
                                    background: isLoading ? '#93c5fd' : '#1e40af', color: '#fff',
                                    fontSize: '15px', fontWeight: 700, cursor: isLoading ? 'not-allowed' : 'pointer',
                                    marginTop: '8px', transition: 'background 0.2s',
                                }}
                            >
                                {isLoading ? 'Verificando...' : 'Acessar Meus Chamados →'}
                            </button>
                        </form>

                        <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '13px', color: '#9ca3af' }}>
                            Ainda não abriu um chamado?{' '}
                            <Link to="/abrir-chamado" style={{ color: '#1e40af', fontWeight: 600, textDecoration: 'none' }}>
                                Clique aqui
                            </Link>
                        </p>
                    </div>
                )}

                {/* DASHBOARD */}
                {viewMode === 'dashboard' && (
                    <div>
                        {/* Resumo */}
                        <div style={{ marginBottom: '32px' }}>
                            <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>
                                Olá! Aqui estão seus chamados 📋
                            </h2>
                            <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                                {chamadosList.length} solicitação(ões) encontrada(s) para <strong>{email}</strong>
                            </p>
                        </div>

                        {/* Cards de resumo */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '32px' }}>
                            {[
                                { label: 'Abertos', count: colunas.abertos.length, color: '#3b82f6', bg: '#eff6ff', icon: '🔵' },
                                { label: 'Em Andamento', count: colunas.emAndamento.length, color: '#f59e0b', bg: '#fffbeb', icon: '⚙️' },
                                { label: 'Concluídos', count: colunas.concluidos.length, color: '#10b981', bg: '#ecfdf5', icon: '✅' },
                            ].map(item => (
                                <div key={item.label} style={{
                                    background: item.bg, border: `1px solid ${item.color}30`,
                                    borderRadius: '14px', padding: '20px', textAlign: 'center',
                                }}>
                                    <div style={{ fontSize: '28px', marginBottom: '6px' }}>{item.icon}</div>
                                    <div style={{ fontSize: '28px', fontWeight: 800, color: item.color }}>{item.count}</div>
                                    <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: 600 }}>{item.label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Controle de Abas */}
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #e5e7eb', paddingBottom: '10px' }}>
                            <button 
                                onClick={() => setActiveTab('ativos')}
                                style={{
                                    padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 700,
                                    border: 'none', background: activeTab === 'ativos' ? '#1e40af' : 'transparent',
                                    color: activeTab === 'ativos' ? '#fff' : '#6b7280', transition: 'all 0.2s'
                                }}
                            >
                                ⏳ Chamados Ativos ({(colunas.abertos.length + colunas.emAndamento.length)})
                            </button>
                            <button 
                                onClick={() => setActiveTab('historico')}
                                style={{
                                    padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 700,
                                    border: 'none', background: activeTab === 'historico' ? '#10b981' : 'transparent',
                                    color: activeTab === 'historico' ? '#fff' : '#6b7280', transition: 'all 0.2s'
                                }}
                            >
                                ✅ Histórico Concluído ({colunas.concluidos.length})
                            </button>
                        </div>

                        {/* Lista de Chamados */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {activeTab === 'ativos' && (
                                <>
                                    {colunas.abertos.length === 0 && colunas.emAndamento.length === 0 ? (
                                        <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', background: '#fff', borderRadius: '12px', border: '1px dashed #d1d5db' }}>
                                            🎉 Você não tem nenhum chamado ativo no momento.
                                        </div>
                                    ) : (
                                        [...colunas.abertos, ...colunas.emAndamento]
                                            .sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds)
                                            .map(c => <ChamadoCard key={c.id} chamado={c} />)
                                    )}
                                </>
                            )}

                            {activeTab === 'historico' && (
                                <>
                                    {colunas.concluidos.length === 0 ? (
                                        <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', background: '#fff', borderRadius: '12px', border: '1px dashed #d1d5db' }}>
                                            Nenhum chamado concluído ainda.
                                        </div>
                                    ) : (
                                        colunas.concluidos
                                            .sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds)
                                            .map(c => <ChamadoCard key={c.id} chamado={c} />)
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MeusChamados;